import { ChevronRightIcon } from "@radix-ui/react-icons";
import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import {
  FormTemplateData,
  useDeleteFormTemplate,
  useDeleteFormTemplateConfirmation,
  useDuplicateFormTemplate,
  useGetFormTemplate,
  useUpdateFormTemplate,
} from "src/api/Forms/forms";
import { useGetSpaces } from "src/api/Spaces/spaces";
import InputHeading from "src/components/ui/Headings/InputHeading";
import { Button } from "src/components/ui/button";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "src/components/ui/dialog";
import { TextInput } from "src/components/ui/text-input";
import { SpaceNameAndSymbol } from "../spaces/spaces";
import "./styles.scss";

type Props = {};

export default function FormTemplateSettings({}: Props) {
  const { formTemplateId } = useParams();
  const { data: formTemplate } = useGetFormTemplate(String(formTemplateId));
  const { data: spaces } = useGetSpaces();
  const space = spaces?.find((s) => s._id === formTemplate?.spaceId);
  return (
    <div className="flex h-full flex-col overflow-auto">
      <div className="flex h-12 shrink-0 items-center justify-between border-b border-gray-200/70 px-8">
        <div className="flex items-center space-x-2 text-sm">
          {space && (
            <SpaceNameAndSymbol name={space.name} color={space.color} />
          )}
          <ChevronRightIcon className="h-3 w-3 text-gray-500" />
          <Link to={`/spaces/${formTemplate?.spaceId}/templates`}>
            <div className="text-gray-800">{"Templates"}</div>
          </Link>
          <ChevronRightIcon className="h-3 w-3 text-gray-500" />
          <Link to={`/spaces/${formTemplate?.spaceId}/templates/forms`}>
            <div className="text-gray-800">{"Forms"}</div>
          </Link>
          <ChevronRightIcon className="h-3 w-3 text-gray-500" />
          <Link
            to={`/spaces/${formTemplate?.spaceId}/templates/forms/${formTemplateId}`}
          >
            <div className="text-gray-800">{formTemplate?.title}</div>
          </Link>
          <ChevronRightIcon className="h-3 w-3 text-gray-500" />
          <div className="cursor-default text-gray-800">Settings</div>
        </div>
      </div>
      <Settings formTemplateId={String(formTemplateId)} />
    </div>
  );
}

function Settings({ formTemplateId }: { formTemplateId: string }) {
  const { data: formTemplate } = useGetFormTemplate(formTemplateId);
  const [
    isDeleteFormTemplateConfirmationOpen,
    setIsDeleteFormTemplateConfirmationOpen,
  ] = useState(false);
  const deleteFormTemplateConfirmationMutation =
    useDeleteFormTemplateConfirmation();
  const deleteFormTemplateMutation = useDeleteFormTemplate();
  const [dependencies, setDependencies] = useState<
    { _id: string; title: string }[]
  >([]);

  useEffect(() => {
    if (deleteFormTemplateConfirmationMutation.data) {
      setDependencies(deleteFormTemplateConfirmationMutation.data.dependencies);
    }
  }, [deleteFormTemplateConfirmationMutation.data]);

  useEffect(() => {
    if (deleteFormTemplateMutation.isSuccess) {
      setIsDeleteFormTemplateConfirmationOpen(false);
    }
  }, [deleteFormTemplateMutation.isSuccess]);

  return (
    <div className="w-full space-y-14 px-8 pt-16">
      <div className="mx-auto max-w-2xl space-y-1">
        <label className="w-40 shrink-0 text-lg font-medium leading-none text-gray-800">
          General
        </label>
        <div className="rounded-md border p-6 shadow-sm">
          <div className="space-y-1">
            <InputHeading heading="Name" />
            {!!formTemplate && (
              <EditableFormTemplateName formTemplate={formTemplate} />
            )}
          </div>
        </div>
      </div>
      <div className="mx-auto max-w-2xl space-y-2.5">
        <label className="w-40 shrink-0 text-lg font-medium leading-none text-gray-800">
          Actions
        </label>
        <div className="space-y-6 divide-y rounded-md border p-6 shadow-sm">
          {formTemplate && (
            <DuplicateFormTemplate
              previousName={formTemplate.title}
              formTemplate={formTemplate}
            />
          )}
          <div className="flex items-center justify-between pt-6">
            <div className="space-y-0.5">
              <label className="leading-none text-gray-700">Delete</label>
              <p className="text-sm font-light text-gray-500">
                Deleting a form template is a permanent action
              </p>
            </div>
            <div>
              <Dialog
                open={isDeleteFormTemplateConfirmationOpen}
                onOpenChange={(open) => {
                  setIsDeleteFormTemplateConfirmationOpen(open);
                }}
              >
                <DialogTrigger asChild>
                  <Button
                    variant={"destructive"}
                    onClick={() => {
                      setIsDeleteFormTemplateConfirmationOpen(true);
                    }}
                  >
                    Delete
                  </Button>
                </DialogTrigger>
                <DialogContent>
                  <DialogHeader>
                    <DialogTitle>Delete Confirmation</DialogTitle>
                    {/* <DialogDescription>
                Confirm deletion of stage template
              </DialogDescription> */}
                  </DialogHeader>
                  <div className="space-y-5 py-4 text-sm text-gray-600">
                    <div>
                      Are you sure you want to delete the form template{" "}
                      <span className="font-medium text-gray-900">
                        {formTemplate?.title}
                      </span>
                      ?
                    </div>
                  </div>
                  <DialogFooter>
                    <DialogClose asChild className="">
                      <div className="flex justify-end space-x-3">
                        <Button
                          variant={"secondary"}
                          onClick={() => {
                            setIsDeleteFormTemplateConfirmationOpen(false);
                          }}
                        >
                          Cancel
                        </Button>
                        <Button
                          variant={"destructive"}
                          onClick={() => {
                            if (formTemplate?._id) {
                              deleteFormTemplateMutation.mutate({
                                formTemplate: {
                                  _id: formTemplate?._id,
                                  force: true,
                                },
                                metadata: {
                                  spaceId: formTemplate?.spaceId.toString(),
                                },
                              });
                            }
                          }}
                        >
                          {deleteFormTemplateMutation.isLoading
                            ? "Deleting..."
                            : "Delete"}
                        </Button>
                      </div>
                    </DialogClose>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
            {/* <Button
            text={isConfirmingDelete ? "Confirm Delete" : "Delete"}
            ring={false}
            fill="outline"
            size="xs"
            color={isConfirmingDelete ? "danger" : "primary"}
            onClick={() => {
              if (isConfirmingDelete) {
                deleteJourneyTemplateMutation.mutate({
                  params: {
                    _id: new ObjectID(journeyTemplateId),
                  },
                });
              } else {
                setIsConfirmingDelete(true);
              }
            }}
          /> */}
          </div>

          {/* <div className="flex">
          <label className="w-40 shrink-0 text-sm text-gray-400">
            Font Type
          </label>
          {!!settings && <FontType settings={settings} />}
        </div> */}
        </div>
      </div>
    </div>
    // <div className="w-full flex-1 space-y-16 overflow-auto px-8 py-[88px]">
    //   <div className="mx-auto max-w-2xl space-y-2">
    //     <div className="">
    //       <label className="w-40 shrink-0 text-lg font-medium leading-none text-gray-800">
    //         General
    //       </label>
    //     </div>
    //     <div className="space-y-6 rounded-md border p-8 shadow-sm">
    //       <div className="space-y-0.5">
    //         <InputHeading heading="Name" />
    //         {formTemplate && (
    //           <EditableFormTemplateName formTemplate={formTemplate} />
    //         )}
    //       </div>
    //     </div>
    //   </div>
    //   <div className="mx-auto max-w-2xl space-y-2">
    //     <label className="w-40 shrink-0 text-lg font-medium leading-none text-gray-800">
    //       Actions
    //     </label>
    //     <div className="space-y-6 divide-y rounded-md border p-8 shadow-sm">
    //       <div className="flex items-center justify-between">
    //         <div className="space-y-0.5">
    //           <label className="leading-none text-gray-600">Delete</label>
    //           <p className="text-sm font-light text-gray-500">
    //             Deleting a project is a permanent action
    //           </p>
    //         </div>
    //         <Button
    //           variant={"destructive"}
    //           onClick={() => {
    //             setIsDeleteFormTemplateConfirmationOpen(true);
    //           }}
    //           size={"sm"}
    //         >
    //           Delete
    //         </Button>
    //       </div>
    //     </div>
    //   </div>
    //   <Dialog
    //     open={isDeleteFormTemplateConfirmationOpen}
    //     onOpenChange={(open) => {
    //       setIsDeleteFormTemplateConfirmationOpen(open);
    //     }}
    //   >
    //     <DialogContent>
    //       <DialogHeader>
    //         <DialogTitle>Delete Confirmation</DialogTitle>
    //         {/* <DialogDescription>
    //           Confirm deletion of stage template
    //         </DialogDescription> */}
    //       </DialogHeader>
    //       <div className="space-y-5 pt-3 text-sm text-gray-600">
    //         <div>
    //           Are you sure you want to delete the form template{" "}
    //           <span className="font-medium text-gray-900">
    //             {formTemplate?.title}
    //           </span>
    //           ?
    //         </div>
    //         {dependencies.length > 0 && (
    //           <div className="space-y-1">
    //             <div>
    //               <span>
    //                 The following task templates will no longer have forms
    //                 attached to them:
    //               </span>
    //             </div>
    //             <ul className="list-disc space-y-1 pl-6">
    //               {dependencies.map((d) => (
    //                 <li key={d._id.toString()}>{d.title}</li>
    //               ))}
    //             </ul>
    //           </div>
    //         )}
    //       </div>
    //       <DialogFooter>
    //         <DialogClose asChild className="">
    //           <div className="flex justify-end space-x-3">
    //             <Button
    //               variant={"secondary"}
    //               onClick={() => {
    //                 setIsDeleteFormTemplateConfirmationOpen(false);
    //               }}
    //             >
    //               Cancel
    //             </Button>
    //             <Button
    //               variant={"destructive"}
    //               onClick={() => {
    //                 if (formTemplate?._id) {
    //                   deleteFormTemplateMutation.mutate({
    //                     _id: formTemplate?._id,
    //                     force: true,
    //                   });
    //                 }
    //               }}
    //             >
    //               {deleteFormTemplateMutation.isLoading
    //                 ? "Deleting..."
    //                 : "Delete"}
    //             </Button>
    //           </div>
    //         </DialogClose>
    //       </DialogFooter>
    //     </DialogContent>
    //   </Dialog>
    // </div>

    // <div className="w-full divide-y divide-gray-300 px-8 pt-6">
    //   <div className="space-y-6 pb-6">
    //     <label className="text-md w-40 shrink-0 font-medium text-gray-600">
    //       Actions
    //     </label>

    //     <div className="flex">
    //       <label className="w-40 shrink-0 text-sm text-gray-400">Delete</label>

    //     </div>
    //   </div>
    // </div>
  );
}

function EditableFormTemplateName({
  formTemplate,
}: {
  formTemplate: FormTemplateData;
}) {
  const [title, setTitle] = useState(formTemplate.title);
  const updateFormTemplateMutation = useUpdateFormTemplate();
  return (
    <TextInput
      className="w-72"
      value={title}
      onChange={(e) => setTitle(e.target.value)}
      onBlur={() => {
        if (
          formTemplate._id &&
          title.trim() !== formTemplate.title &&
          title.trim() !== ""
        ) {
          updateFormTemplateMutation.mutate({
            _id: formTemplate._id,
            title,
          });
        } else {
          setTitle(formTemplate.title);
        }
      }}
    />
  );
}

const DuplicateFormTemplate = ({
  previousName,
  formTemplate,
}: {
  previousName: string;
  formTemplate: FormTemplateData;
}) => {
  const { formTemplateId, spaceId } = useParams();
  const duplicateFormTemplateMutation = useDuplicateFormTemplate();
  const [isDuplicating, setIsDuplicating] = useState(false);
  const [newTemplateName, setNewTemplateName] = useState(
    previousName + " Copy"
  );

  useEffect(() => {
    if (duplicateFormTemplateMutation.isSuccess) {
      setIsDuplicating(false);
    }
  }, [duplicateFormTemplateMutation.isSuccess]);

  return (
    <div className="flex items-center justify-between">
      <div className="space-y-0.5">
        <label className="leading-none text-gray-700">Duplicate</label>
        <p className="text-sm font-light text-gray-500">
          Duplicate this form template and all of its fields
        </p>
      </div>
      <Dialog
        open={isDuplicating}
        onOpenChange={(open) => {
          setIsDuplicating(open);
        }}
      >
        <DialogTrigger asChild>
          <Button
            onClick={() => {
              setIsDuplicating(true);
            }}
            variant={"secondary"}
          >
            Duplicate
          </Button>
        </DialogTrigger>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Duplicate Form Template</DialogTitle>
          </DialogHeader>
          <div className="space-y-6 py-3">
            <div className="space-y-1">
              <InputHeading heading="New Template Name" />
              <TextInput
                value={newTemplateName}
                onChange={(e) => {
                  setNewTemplateName(e.target.value);
                }}
                className="max-w-xs"
              />
            </div>
          </div>
          <div className="flex justify-end space-x-2">
            {/* <Button
              variant={"secondary"}
              onClick={() => {
                setIsDuplicating(false);
              }}
            >
              Cancel
            </Button> */}
            <Button
              disabled={
                duplicateFormTemplateMutation.isLoading ||
                newTemplateName.trim() === ""
              }
              onClick={() => {
                if (formTemplateId)
                  duplicateFormTemplateMutation.mutate({
                    metadata: {
                      spaceId: formTemplate.spaceId,
                    },
                    formTemplate: {
                      formTemplateId: String(formTemplateId),
                      title: newTemplateName,
                    },
                  });
              }}
            >
              {duplicateFormTemplateMutation.isLoading
                ? "Duplicating.."
                : "Duplicate"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* <Modal
        isOpen={isDuplicating}
        closeModal={() => {
          setIsDuplicating(false);
        }}
      >
        <div className="p-6">
          <ModalHeading
            title="Duplicate Journey Template"
            subtitle="Duplicate this journey template and all of its tasks."
          />
          <div className="mt-6 space-y-6">
            <TextInput
              label="New Template Name"
              value={newTemplateName}
              name="newTemplateName"
              onChange={(e) => {
                setNewTemplateName(e.target.value);
              }}
            />
            <div className="flex justify-end">
              <Button
                disabled={
                  duplicateJourneyTemplateMutation.isLoading ||
                  newTemplateName.trim() === ""
                }
                onClick={() => {
                  if (journeyTemplateId)
                    duplicateJourneyTemplateMutation.mutate({
                      _id: journeyTemplateId,
                      name: newTemplateName,
                    });
                }}
              >
                Duplicate
              </Button>
            </div>
          </div>
        </div>
      </Modal> */}
    </div>
  );
};
